import React from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

// Shared building blocks for the Diligent Risk Model editors (admin template
// editor and sponsor custom-model editor): 1-5 anchored steppers, scale
// tooltips, template picker options, and the section accordion header.

export const naturalCompare = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

// Hover copy for one dimension at one level, with the methodology anchor.
export const anchorTitle = (dimName, level, anchorMap, inverted) => {
  const lines = [];
  if (level != null) {
    lines.push(`${dimName}: ${level} / 5`);
    const a = anchorMap?.[level];
    if (a && (a.anchor || a.definition)) {
      lines.push([a.anchor, a.definition].filter(Boolean).join(" — "));
    } else {
      lines.push("Methodology anchor copy pending.");
    }
  }
  if (inverted) lines.push("Inverted scale: higher number = harder to detect = more risk.");
  return lines.join("\n");
};

// Full 1-5 scale for a dimension — shown on the column-header info icon.
export const fullScaleTitle = (dimName, anchorMap, inverted) => {
  const lines = [`${dimName} — 1 to 5`];
  for (let n = 1; n <= 5; n += 1) {
    const a = anchorMap?.[n];
    lines.push(`${n}: ${a ? [a.anchor, a.definition].filter(Boolean).join(" — ") : "(anchor pending)"}`);
  }
  if (inverted) lines.push("Inverted: 5 = hardest to catch = most risk.");
  return lines.join("\n");
};

export const Pill = ({ label, styles }) =>
  label ? (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        styles[label] || "bg-gray-100 text-gray-600 border-gray-200"
      }`}
    >
      {label}
    </span>
  ) : (
    <span className="text-gray-300">—</span>
  );

const STEPPER_TONES = {
  amber: {
    box: "border-amber-400 bg-amber-50 ring-1 ring-amber-200",
    num: "text-amber-700",
  },
  violet: {
    box: "border-violet-400 bg-violet-50 ring-1 ring-violet-200",
    num: "text-violet-700",
  },
};

/**
 * 1-5 stepper with the verbatim anchored label on hover and the active anchor
 * word under it (so Detectability's inverted scale is unmistakable). The
 * highlight semantics stay with the caller: the admin editor highlights
 * changed-from-saved (amber), the sponsor editor highlights overridden-from-
 * base (violet) and maps stepping back onto the base to "clear override".
 */
export const AnchoredValueStepper = ({
  value,
  dimName,
  anchors,
  inverted,
  highlighted,
  tone = "amber",
  onStep,
}) => {
  const active = value != null ? anchors?.[value] : null;
  const clamp = (n) => Math.max(1, Math.min(5, n));
  const toneCls = STEPPER_TONES[tone] || STEPPER_TONES.amber;
  return (
    <div className="flex flex-col items-center gap-0.5 min-w-0">
      <div
        title={anchorTitle(dimName, value, anchors, inverted)}
        className={`inline-flex items-center rounded-md border overflow-hidden ${
          highlighted ? toneCls.box : "border-gray-300 bg-white"
        }`}
      >
        <button
          type="button"
          onClick={() => value != null && onStep(clamp(value - 1))}
          disabled={value == null || value <= 1}
          className="px-2 py-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label={`decrease ${dimName}`}
        >
          −
        </button>
        <span
          className={`w-7 text-center text-sm font-semibold tabular-nums ${
            highlighted ? toneCls.num : "text-gray-800"
          }`}
        >
          {value ?? "–"}
        </span>
        <button
          type="button"
          onClick={() => onStep(clamp((value ?? 0) + 1))}
          disabled={value >= 5}
          className="px-2 py-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label={`increase ${dimName}`}
        >
          +
        </button>
      </div>
      {active?.anchor ? (
        <span
          className="text-[9px] leading-tight text-gray-400 text-center truncate max-w-[6rem]"
          title={active.definition || active.anchor}
        >
          {active.anchor}
        </span>
      ) : null}
    </div>
  );
};

export const ColHeader = ({ label, anchors, inverted, dimName }) => (
  <div className="flex items-center justify-center gap-1">
    <span>{label}</span>
    <span title={fullScaleTitle(dimName, anchors, inverted)} className="cursor-help">
      <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />
    </span>
  </div>
);

/** Sorted template <option> list shared by both editors' pickers. */
export const TemplateOptions = ({ templates }) =>
  (templates || [])
    .slice()
    .sort((a, b) => naturalCompare(a.template_name, b.template_name))
    .map((t) => (
      <option key={t.diligent_risk_template_id} value={t.diligent_risk_template_id}>
        {t.template_name}
        {t.template_family ? ` (${t.template_family})` : ""}
      </option>
    ));

/**
 * Collapsible section header row. The right-side aggregate cluster differs per
 * editor, so it renders as children after the shared chevron + name + count.
 */
export const SectionToggleHeader = ({ sectionName, count, isCollapsed, onToggle, children }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center gap-2 px-4 py-2 bg-indigo-50/60 hover:bg-indigo-50 border-b border-gray-200 text-left"
  >
    {isCollapsed ? (
      <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
    )}
    <span className="text-[13px] font-semibold text-gray-800 truncate">{sectionName}</span>
    <span className="text-[11px] text-gray-400">({count})</span>
    {children}
  </button>
);
